import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  useWatch,
} from "react-hook-form";
import { gemsOf, type GemPackFormValues } from "./values";

type Props = {
  control: Control<GemPackFormValues>;
  register: UseFormRegister<GemPackFormValues>;
  errors: FieldErrors<GemPackFormValues>;
  /** Edit: the key is shown, never sent. Purchases, offers and the shop name a pack by it. */
  keyLocked?: boolean;
};

const whole = (label: string, min: number, max: number) => ({
  required: `${label} is required`,
  valueAsNumber: true,
  validate: (v: number) =>
    (Number.isInteger(v) && v >= min && v <= max) || `${label} must be a whole number from ${min} to ${max.toLocaleString()}`,
});

export const GemPackFields = ({ control, register, errors, keyLocked = false }: Props) => {
  const [credits, bonus] = useWatch({ control, name: ["creditsAmount", "bonusCredits"] });
  const { gems, percent } = gemsOf(credits, bonus);

  return (
    <Box component="form" sx={{ display: "flex", flexDirection: "column" }} autoComplete="off">
      <TextField
        {...register("key", {
          required: keyLocked ? false : "Key is required",
          pattern: keyLocked
            ? undefined
            : { value: /^[a-z0-9][a-z0-9-]{0,31}$/, message: "Lowercase letters, digits and dashes, at most 32" },
        })}
        disabled={keyLocked}
        error={!!errors?.key}
        helperText={
          errors?.key?.message?.toString() ??
          (keyLocked
            ? "The pack's id. Purchases and offers name it, so it never changes."
            : "The pack's id (e.g. pouch). Purchases and offers name it, so it cannot be changed later.")
        }
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        label="Key"
      />

      <TextField
        {...register("name", {
          required: "Name is required",
          validate: (v: string) => (v.trim().length > 0 && v.trim().length <= 40) || "1 to 40 characters",
        })}
        error={!!errors?.name}
        helperText={errors?.name?.message?.toString() ?? "What the shop calls the pack."}
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        label="Name"
      />

      <TextField
        {...register("creditsAmount", whole("Gems", 1, 1_000_000))}
        error={!!errors?.creditsAmount}
        helperText={errors?.creditsAmount?.message?.toString()}
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        type="number"
        label="Gems"
      />

      <TextField
        {...register("bonusCredits", whole("Bonus gems", 0, 1_000_000))}
        error={!!errors?.bonusCredits}
        helperText={errors?.bonusCredits?.message?.toString()}
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        type="number"
        label="Bonus gems"
      />

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        The shop sells {gems.toLocaleString()} gems
        {percent ? `, shown as ${percent}% more` : ""}.
      </Typography>

      <TextField
        {...register("priceUsd", {
          required: "Price is required",
          valueAsNumber: true,
          validate: (v: number) => (Number.isFinite(v) && v >= 0 && v <= 9999.99) || "From 0 to 9,999.99",
        })}
        error={!!errors?.priceUsd}
        helperText={errors?.priceUsd?.message?.toString() ?? "Shown in the shop. Purchases are still test purchases: nothing is charged."}
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        type="number"
        label="Price (USD)"
        inputProps={{ step: "0.01", min: 0 }}
      />

      <TextField
        {...register("sortOrder", whole("Sort order", 0, 100_000))}
        error={!!errors?.sortOrder}
        helperText={errors?.sortOrder?.message?.toString() ?? "The shop lists packs from the lowest."}
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        type="number"
        label="Sort order"
      />

      <TextField
        {...register("appleProductId")}
        error={!!errors?.appleProductId}
        helperText={errors?.appleProductId?.message?.toString() ?? "Optional: the App Store product this pack will be sold as."}
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        label="App Store product ID"
      />

      <TextField
        {...register("googleProductId")}
        error={!!errors?.googleProductId}
        helperText={errors?.googleProductId?.message?.toString() ?? "Optional: the Google Play product this pack will be sold as."}
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        label="Google Play product ID"
      />

      <Controller
        control={control}
        name="isBest"
        render={({ field }) => (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={!!field.value} onChange={(_, checked) => field.onChange(checked)} />}
            label="Best value (the shop marks it)"
          />
        )}
      />

      <Controller
        control={control}
        name="isActive"
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox checked={!!field.value} onChange={(_, checked) => field.onChange(checked)} />}
            label="On sale"
          />
        )}
      />
    </Box>
  );
};
